/**
 * US Retirement Planner Component
 * Full-featured retirement planning with Monte Carlo simulation
 *
 * Features:
 * - 401(k)/IRA/Roth/HSA account management
 * - Social Security optimization
 * - Roth conversion strategies
 * - Tax-efficient withdrawal sequencing
 * - Medicare IRMAA awareness
 * - Monte Carlo simulation
 */

import React, { useState, useCallback } from 'react';
import { useCountry } from '../contexts/CountryContext';
import InfoTooltip from './shared/InfoTooltip';
import {
  USRetirementAPI,
  USUserProfile,
  USSimulationResponse,
  USQuickEstimateResponse,
  WithdrawalStrategyResponse,
} from '../services/us-retirement-api';
import './USRetirementPlanner.css';

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

// No income tax states
const NO_TAX_STATES = ['AK', 'FL', 'NV', 'NH', 'SD', 'TN', 'TX', 'WA', 'WY'];

type TabType = 'inputs' | 'results' | 'strategies' | 'milestones';
type InputSection = 'demographics' | 'accounts' | 'income' | 'social-security' | 'spending' | 'strategies';

const USRetirementPlanner: React.FC = () => {
  const { currencyCode } = useCountry();

  // ==================== STATE ====================
  const [activeTab, setActiveTab] = useState<TabType>('inputs');
  const [activeInputSection, setActiveInputSection] = useState<InputSection>('demographics');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Results
  const [simulationResults, setSimulationResults] = useState<USSimulationResponse | null>(null);
  const [quickEstimate, setQuickEstimate] = useState<USQuickEstimateResponse | null>(null);
  const [withdrawalStrategies, setWithdrawalStrategies] = useState<WithdrawalStrategyResponse | null>(null);

  // User Profile State - Demographics
  const [age, setAge] = useState(35);
  const [gender, setGender] = useState<'Male' | 'Female'>('Male');
  const [state, setState] = useState('CA');
  const [filingStatus, setFilingStatus] = useState<'single' | 'married'>('single');
  const [lifeExpectancyScenario, setLifeExpectancyScenario] = useState<1 | 2 | 3>(2);

  // Spouse
  const [hasSpouse, setHasSpouse] = useState(false);
  const [spouseAge, setSpouseAge] = useState(33);
  const [spouseGender, setSpouseGender] = useState<'Male' | 'Female'>('Female');

  // Income
  const [currentIncome, setCurrentIncome] = useState(100000);
  const [retirementAge, setRetirementAge] = useState(65);
  const [spouseIncome, setSpouseIncome] = useState(80000);
  const [spouseRetirementAge, setSpouseRetirementAge] = useState(65);

  // Account Balances
  const [traditional401k, setTraditional401k] = useState(150000);
  const [traditionalIRA, setTraditionalIRA] = useState(0);
  const [roth401k, setRoth401k] = useState(0);
  const [rothIRA, setRothIRA] = useState(50000);
  const [rothBasis, setRothBasis] = useState(30000);
  const [hsa, setHsa] = useState(15000);
  const [taxableBrokerage, setTaxableBrokerage] = useState(100000);
  const [taxableBasis, setTaxableBasis] = useState(80000);

  // Spouse Accounts
  const [spouseTraditional401k, setSpouseTraditional401k] = useState(100000);
  const [spouseTraditionalIRA, setSpouseTraditionalIRA] = useState(0);
  const [spouseRoth401k, setSpouseRoth401k] = useState(0);
  const [spouseRothIRA, setSpouseRothIRA] = useState(30000);
  const [spouseHsa, setSpouseHsa] = useState(10000);

  // Annual Contributions
  const [contribution401k, setContribution401k] = useState(23000);
  const [contributionRoth401k, setContributionRoth401k] = useState(0);
  const [contributionIRA, setContributionIRA] = useState(0);
  const [contributionRothIRA, setContributionRothIRA] = useState(7000);
  const [contributionHSA, setContributionHSA] = useState(4150);

  // Investment Assumptions
  const [stockReturn, setStockReturn] = useState(7.0);
  const [bondReturn, setBondReturn] = useState(4.0);
  const [stockAllocation, setStockAllocation] = useState(70);
  const [volatility, setVolatility] = useState(12);
  const [inflationRate, setInflationRate] = useState(2.5);

  // Spending
  const [annualSpending, setAnnualSpending] = useState(60000);
  const [healthcareCost, setHealthcareCost] = useState(6000);
  const [spendingScenario, setSpendingScenario] = useState<'exact' | 'fluctuating' | 'increased'>('exact');

  // Social Security
  const [ssClaimingStrategy, setSSClaimingStrategy] = useState<'early' | 'fra' | 'delayed' | 'optimize'>('optimize');
  const [customSSAge, setCustomSSAge] = useState(67);
  const [estimatedPIA, setEstimatedPIA] = useState<number | undefined>(undefined);
  const [ssWorkYears, setSSWorkYears] = useState(20);
  const [spouseSSStrategy, setSpouseSSStrategy] = useState<'early' | 'fra' | 'delayed' | 'optimize'>('optimize');
  const [spouseCustomSSAge, setSpouseCustomSSAge] = useState(67);
  const [spouseEstimatedPIA, setSpouseEstimatedPIA] = useState<number | undefined>(undefined);

  // Pension
  const [hasPension, setHasPension] = useState(false);
  const [pensionAmount, setPensionAmount] = useState(0);
  const [pensionStartAge, setPensionStartAge] = useState(65);
  const [pensionCOLA, setPensionCOLA] = useState(0);

  // Strategies
  const [withdrawalStrategy, setWithdrawalStrategy] = useState<'traditional_first' | 'roth_first' | 'proportional' | 'tax_efficient'>('tax_efficient');
  const [rothConversionStrategy, setRothConversionStrategy] = useState<'none' | 'fill_bracket' | 'avoid_irmaa' | 'aggressive'>('fill_bracket');
  const [avoidIRMAA, setAvoidIRMAA] = useState(true);

  // Simulation Settings
  const [numScenarios, setNumScenarios] = useState(100);

  // ==================== HELPERS ====================
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const parseNumber = (value: string): number => {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // ==================== API CALLS ====================
  const buildUserProfile = (): USUserProfile => {
    const currentYear = new Date().getFullYear();
    const profile: USUserProfile = {
      age,
      gender,
      birth_year: currentYear - age,
      state: US_STATES.find(s => s.code === state)?.name || 'California',
      filing_status: filingStatus,
      life_expectancy_scenario: lifeExpectancyScenario,

      current_income: currentIncome,
      retirement_age: retirementAge,
      inflation_rate: inflationRate,

      traditional_401k_balance: traditional401k,
      traditional_ira_balance: traditionalIRA,
      roth_401k_balance: roth401k,
      roth_ira_balance: rothIRA,
      roth_ira_contribution_basis: rothBasis,
      hsa_balance: hsa,
      taxable_brokerage_balance: taxableBrokerage,
      taxable_brokerage_cost_basis: taxableBasis,

      stock_return_rate: stockReturn,
      bond_return_rate: bondReturn,
      stock_allocation: stockAllocation,
      volatility,

      annual_spending: annualSpending,
      spending_scenario: spendingScenario,
      healthcare_annual_cost: healthcareCost,

      ss_claiming_strategy: ssClaimingStrategy === 'optimize' ? 'optimize' : ssClaimingStrategy,
      ss_claiming_age: ssClaimingStrategy === 'optimize' ? undefined : customSSAge,
      estimated_ss_pia: estimatedPIA,
      ss_earnings_history_years: ssWorkYears,

      pension_amount: hasPension ? pensionAmount : undefined,
      pension_start_age: hasPension ? pensionStartAge : undefined,
      pension_cola: pensionCOLA,

      withdrawal_strategy: withdrawalStrategy,
      roth_conversion_strategy: rothConversionStrategy,
      avoid_irmaa: avoidIRMAA,
    };

    // Add spouse info if married
    if (hasSpouse && filingStatus === 'married') {
      profile.spouse_age = spouseAge;
      profile.spouse_gender = spouseGender;
      profile.spouse_birth_year = currentYear - spouseAge;
      profile.spouse_current_income = spouseIncome;
      profile.spouse_retirement_age = spouseRetirementAge;
      profile.spouse_traditional_401k_balance = spouseTraditional401k;
      profile.spouse_traditional_ira_balance = spouseTraditionalIRA;
      profile.spouse_roth_401k_balance = spouseRoth401k;
      profile.spouse_roth_ira_balance = spouseRothIRA;
      profile.spouse_hsa_balance = spouseHsa;
      profile.spouse_ss_claiming_strategy = spouseSSStrategy;
      profile.spouse_ss_claiming_age = spouseSSStrategy === 'optimize' ? undefined : spouseCustomSSAge;
      profile.spouse_estimated_ss_pia = spouseEstimatedPIA;
    }

    // Add income stages with contributions
    profile.income_stages = [{
      stage: 1,
      starting_age: age,
      ending_age: retirementAge - 1,
      starting_income: currentIncome,
      ending_income: currentIncome * Math.pow(1.02, retirementAge - age - 1),
      traditional_401k_contribution: contribution401k,
      roth_401k_contribution: contributionRoth401k,
      traditional_ira_contribution: contributionIRA,
      roth_ira_contribution: contributionRothIRA,
      hsa_contribution: contributionHSA,
    }];

    return profile;
  };

  // Input validation before API calls
  const validateInputs = (): string | null => {
    if (age < 18 || age > 85) {
      return 'Age must be between 18 and 85';
    }
    if (retirementAge <= age) {
      return 'Retirement age must be greater than current age';
    }
    if (retirementAge > 85) {
      return 'Retirement age cannot exceed 85';
    }
    if (currentIncome < 0) {
      return 'Income cannot be negative';
    }
    if (annualSpending < 0) {
      return 'Annual spending cannot be negative';
    }
    if (annualSpending > currentIncome * 2) {
      return 'Annual spending seems unusually high compared to income. Please verify.';
    }
    if (hasSpouse && filingStatus === 'married') {
      if (spouseAge < 18 || spouseAge > 85) {
        return 'Spouse age must be between 18 and 85';
      }
      if (spouseRetirementAge <= spouseAge) {
        return 'Spouse retirement age must be greater than spouse current age';
      }
    }
    return null; // No errors
  };

  const runSimulation = useCallback(async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profile = buildUserProfile();
      const result = await USRetirementAPI.runSimulation({
        user_profile: profile,
        num_scenarios: numScenarios,
        year: 2024,
      });
      setSimulationResults(result);
      setActiveTab('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [age, gender, state, filingStatus, currentIncome, retirementAge, traditional401k, rothIRA, annualSpending, numScenarios]);

  const getQuickEstimate = useCallback(async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const totalSavings = traditional401k + traditionalIRA + roth401k + rothIRA + hsa + taxableBrokerage;
      const annualSavings = contribution401k + contributionRoth401k + contributionIRA + contributionRothIRA + contributionHSA;

      const result = await USRetirementAPI.getQuickEstimate({
        age,
        retirement_age: retirementAge,
        current_income: currentIncome,
        annual_savings: annualSavings,
        current_savings: totalSavings,
        expected_return: stockReturn * (stockAllocation / 100) + bondReturn * (1 - stockAllocation / 100),
        inflation_rate: inflationRate,
        life_expectancy: lifeExpectancyScenario === 1 ? 95 : lifeExpectancyScenario === 2 ? 90 : 85,
      });
      setQuickEstimate(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quick estimate failed');
    } finally {
      setLoading(false);
    }
  }, [age, retirementAge, currentIncome, traditional401k, rothIRA, stockReturn]);

  const compareStrategies = useCallback(async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const traditionalTotal = traditional401k + traditionalIRA + (hasSpouse ? spouseTraditional401k + spouseTraditionalIRA : 0);
      const rothTotal = roth401k + rothIRA + (hasSpouse ? spouseRoth401k + spouseRothIRA : 0);

      const result = await USRetirementAPI.compareWithdrawalStrategies(
        traditionalTotal,
        rothTotal,
        taxableBrokerage,
        annualSpending,
        22 // Assumed marginal rate
      );
      setWithdrawalStrategies(result);
      setActiveTab('strategies');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Strategy comparison failed');
    } finally {
      setLoading(false);
    }
  }, [traditional401k, rothIRA, taxableBrokerage, annualSpending]);

  // ==================== RENDER HELPERS ====================
  const renderInputSection = () => {
    const sections: { id: InputSection; label: string; icon: string }[] = [
      { id: 'demographics', label: 'Demographics', icon: '👤' },
      { id: 'accounts', label: 'Accounts', icon: '💰' },
      { id: 'income', label: 'Income', icon: '💵' },
      { id: 'social-security', label: 'Social Security', icon: '🏛️' },
      { id: 'spending', label: 'Spending', icon: '🛒' },
      { id: 'strategies', label: 'Strategies', icon: '📊' },
    ];

    return (
      <div className="inputs-container">
        <div className="section-nav">
          {sections.map(section => (
            <button
              key={section.id}
              className={`section-btn ${activeInputSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveInputSection(section.id)}
            >
              <span className="icon">{section.icon}</span>
              <span className="label">{section.label}</span>
            </button>
          ))}
        </div>

        <div className="section-content">
          {activeInputSection === 'demographics' && renderDemographicsSection()}
          {activeInputSection === 'accounts' && renderAccountsSection()}
          {activeInputSection === 'income' && renderIncomeSection()}
          {activeInputSection === 'social-security' && renderSocialSecuritySection()}
          {activeInputSection === 'spending' && renderSpendingSection()}
          {activeInputSection === 'strategies' && renderStrategiesSection()}
        </div>

        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={getQuickEstimate}
            disabled={loading}
          >
            Quick Estimate
          </button>
          <button
            className="btn btn-primary"
            onClick={runSimulation}
            disabled={loading}
          >
            {loading ? 'Running Simulation...' : `Run Monte Carlo (${numScenarios} scenarios)`}
          </button>
        </div>

        {quickEstimate && (
          <div className="quick-estimate-card">
            <h3>Quick Estimate</h3>
            <div className="estimate-grid">
              <div className="estimate-item">
                <span className="label">Years to Retirement</span>
                <span className="value">{quickEstimate.years_to_retirement}</span>
              </div>
              <div className="estimate-item">
                <span className="label">Projected Savings</span>
                <span className="value">{formatCurrency(quickEstimate.projected_savings_at_retirement)}</span>
              </div>
              <div className="estimate-item">
                <span className="label">Safe Withdrawal (4%)</span>
                <span className="value">{formatCurrency(quickEstimate.safe_annual_withdrawal)}</span>
              </div>
              <div className="estimate-item">
                <span className="label">Est. Social Security</span>
                <span className="value">{formatCurrency(quickEstimate.estimated_social_security)}</span>
              </div>
              <div className="estimate-item highlight">
                <span className="label">Total Annual Income</span>
                <span className="value">{formatCurrency(quickEstimate.total_annual_income)}</span>
              </div>
              <div className="estimate-item">
                <span className="label">Replacement Ratio</span>
                <span className="value">{quickEstimate.replacement_ratio}%</span>
              </div>
            </div>
            <div className="estimate-notes">
              {quickEstimate.notes.map((note, i) => (
                <p key={i}>{note}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDemographicsSection = () => (
    <div className="form-section">
      <h3>Your Information</h3>

      <div className="form-row">
        <div className="form-group">
          <label>Age</label>
          <input
            type="number"
            value={age}
            onChange={e => setAge(parseInt(e.target.value) || 35)}
            min={18}
            max={85}
          />
        </div>
        <div className="form-group">
          <label>Gender</label>
          <select value={gender} onChange={e => setGender(e.target.value as 'Male' | 'Female')}>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>State</label>
          <select value={state} onChange={e => setState(e.target.value)}>
            {US_STATES.map(s => (
              <option key={s.code} value={s.code}>
                {s.name} {NO_TAX_STATES.includes(s.code) ? '(No Income Tax)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Filing Status</label>
          <select value={filingStatus} onChange={e => {
            const status = e.target.value as 'single' | 'married';
            setFilingStatus(status);
            if (status === 'married') setHasSpouse(true);
          }}>
            <option value="single">Single</option>
            <option value="married">Married Filing Jointly</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Life Expectancy Assumption</label>
          <select
            value={lifeExpectancyScenario}
            onChange={e => setLifeExpectancyScenario(parseInt(e.target.value) as 1 | 2 | 3)}
          >
            <option value={1}>Conservative (Plan to 95+)</option>
            <option value={2}>Moderate (Plan to 90)</option>
            <option value={3}>Aggressive (Plan to 85)</option>
          </select>
        </div>
      </div>

      {filingStatus === 'married' && (
        <>
          <h3>Spouse Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Spouse Age</label>
              <input
                type="number"
                value={spouseAge}
                onChange={e => setSpouseAge(parseInt(e.target.value) || 33)}
                min={18}
                max={85}
              />
            </div>
            <div className="form-group">
              <label>Spouse Gender</label>
              <select value={spouseGender} onChange={e => setSpouseGender(e.target.value as 'Male' | 'Female')}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderAccountsSection = () => (
    <div className="form-section">
      <h3>Your Retirement Accounts</h3>

      <div className="account-category">
        <h4>Tax-Deferred (Traditional)</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Traditional 401(k)</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={traditional401k.toLocaleString()}
                onChange={e => setTraditional401k(parseNumber(e.target.value))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Traditional IRA</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={traditionalIRA.toLocaleString()}
                onChange={e => setTraditionalIRA(parseNumber(e.target.value))}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="account-category">
        <h4>Tax-Free (Roth)</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Roth 401(k)</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={roth401k.toLocaleString()}
                onChange={e => setRoth401k(parseNumber(e.target.value))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Roth IRA</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={rothIRA.toLocaleString()}
                onChange={e => setRothIRA(parseNumber(e.target.value))}
              />
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Roth IRA Contribution Basis (tax-free portion)</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={rothBasis.toLocaleString()}
                onChange={e => setRothBasis(parseNumber(e.target.value))}
              />
            </div>
            <span className="hint">Total of your Roth contributions (can withdraw tax/penalty free)</span>
          </div>
        </div>
      </div>

      <div className="account-category">
        <h4>Other Accounts</h4>
        <div className="form-row">
          <div className="form-group">
            <label>HSA (Health Savings Account)</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={hsa.toLocaleString()}
                onChange={e => setHsa(parseNumber(e.target.value))}
              />
            </div>
            <span className="hint">Triple tax advantage for medical expenses</span>
          </div>
          <div className="form-group">
            <label>Taxable Brokerage</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={taxableBrokerage.toLocaleString()}
                onChange={e => setTaxableBrokerage(parseNumber(e.target.value))}
              />
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Taxable Account Cost Basis</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={taxableBasis.toLocaleString()}
                onChange={e => setTaxableBasis(parseNumber(e.target.value))}
              />
            </div>
            <span className="hint">Original investment amount (for capital gains calculation)</span>
          </div>
        </div>
      </div>

      <div className="account-category">
        <h4>Annual Contributions</h4>
        <div className="form-row">
          <div className="form-group">
            <label>401(k) Contribution</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={contribution401k.toLocaleString()}
                onChange={e => setContribution401k(parseNumber(e.target.value))}
              />
            </div>
            <span className="hint">2024 limit: $23,000 ($30,500 if 50+)</span>
          </div>
          <div className="form-group">
            <label>Roth IRA Contribution</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={contributionRothIRA.toLocaleString()}
                onChange={e => setContributionRothIRA(parseNumber(e.target.value))}
              />
            </div>
            <span className="hint">2024 limit: $7,000 ($8,000 if 50+)</span>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>HSA Contribution</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={contributionHSA.toLocaleString()}
                onChange={e => setContributionHSA(parseNumber(e.target.value))}
              />
            </div>
            <span className="hint">2024 limit: $4,150 individual / $8,300 family</span>
          </div>
        </div>
      </div>

      {filingStatus === 'married' && (
        <div className="account-category spouse">
          <h4>Spouse Accounts</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Spouse Traditional 401(k)</label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="text"
                  value={spouseTraditional401k.toLocaleString()}
                  onChange={e => setSpouseTraditional401k(parseNumber(e.target.value))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Spouse Roth IRA</label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="text"
                  value={spouseRothIRA.toLocaleString()}
                  onChange={e => setSpouseRothIRA(parseNumber(e.target.value))}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="total-summary">
        <div className="summary-item">
          <span>Total Traditional</span>
          <span className="value">{formatCurrency(traditional401k + traditionalIRA + (hasSpouse ? spouseTraditional401k + spouseTraditionalIRA : 0))}</span>
        </div>
        <div className="summary-item">
          <span>Total Roth</span>
          <span className="value">{formatCurrency(roth401k + rothIRA + (hasSpouse ? spouseRoth401k + spouseRothIRA : 0))}</span>
        </div>
        <div className="summary-item highlight">
          <span>Total Portfolio</span>
          <span className="value">
            {formatCurrency(
              traditional401k + traditionalIRA + roth401k + rothIRA + hsa + taxableBrokerage +
              (hasSpouse ? spouseTraditional401k + spouseTraditionalIRA + spouseRoth401k + spouseRothIRA + spouseHsa : 0)
            )}
          </span>
        </div>
      </div>
    </div>
  );

  const renderIncomeSection = () => (
    <div className="form-section">
      <h3>Income & Employment</h3>

      <div className="form-row">
        <div className="form-group">
          <label>Current Annual Income</label>
          <div className="input-with-prefix">
            <span className="prefix">$</span>
            <input
              type="text"
              value={currentIncome.toLocaleString()}
              onChange={e => setCurrentIncome(parseNumber(e.target.value))}
            />
          </div>
        </div>
        <div className="form-group">
          <label>Planned Retirement Age</label>
          <input
            type="number"
            value={retirementAge}
            onChange={e => setRetirementAge(parseInt(e.target.value) || 65)}
            min={55}
            max={75}
          />
        </div>
      </div>

      {filingStatus === 'married' && (
        <div className="form-row">
          <div className="form-group">
            <label>Spouse Annual Income</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={spouseIncome.toLocaleString()}
                onChange={e => setSpouseIncome(parseNumber(e.target.value))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Spouse Retirement Age</label>
            <input
              type="number"
              value={spouseRetirementAge}
              onChange={e => setSpouseRetirementAge(parseInt(e.target.value) || 65)}
              min={55}
              max={75}
            />
          </div>
        </div>
      )}

      <h3>Investment Assumptions</h3>
      <div className="form-row">
        <div className="form-group">
          <label>Expected Stock Return</label>
          <div className="input-with-suffix">
            <input
              type="number"
              value={stockReturn}
              onChange={e => setStockReturn(parseFloat(e.target.value) || 7)}
              step={0.5}
              min={0}
              max={15}
            />
            <span className="suffix">%</span>
          </div>
        </div>
        <div className="form-group">
          <label>Expected Bond Return</label>
          <div className="input-with-suffix">
            <input
              type="number"
              value={bondReturn}
              onChange={e => setBondReturn(parseFloat(e.target.value) || 4)}
              step={0.5}
              min={0}
              max={10}
            />
            <span className="suffix">%</span>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Stock Allocation</label>
          <div className="input-with-suffix">
            <input
              type="number"
              value={stockAllocation}
              onChange={e => setStockAllocation(parseInt(e.target.value) || 70)}
              min={0}
              max={100}
            />
            <span className="suffix">%</span>
          </div>
          <span className="hint">Bonds: {100 - stockAllocation}%</span>
        </div>
        <div className="form-group">
          <label>Expected Inflation</label>
          <div className="input-with-suffix">
            <input
              type="number"
              value={inflationRate}
              onChange={e => setInflationRate(parseFloat(e.target.value) || 2.5)}
              step={0.1}
              min={0}
              max={10}
            />
            <span className="suffix">%</span>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Pension</label>
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="hasPension"
              checked={hasPension}
              onChange={e => setHasPension(e.target.checked)}
            />
            <label htmlFor="hasPension">I have a pension</label>
          </div>
        </div>
      </div>

      {hasPension && (
        <div className="form-row">
          <div className="form-group">
            <label>Annual Pension Amount</label>
            <div className="input-with-prefix">
              <span className="prefix">$</span>
              <input
                type="text"
                value={pensionAmount.toLocaleString()}
                onChange={e => setPensionAmount(parseNumber(e.target.value))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Pension Start Age</label>
            <input
              type="number"
              value={pensionStartAge}
              onChange={e => setPensionStartAge(parseInt(e.target.value) || 65)}
              min={55}
              max={75}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderSocialSecuritySection = () => (
    <div className="form-section">
      <h3>Social Security - Your Benefits</h3>

      <div className="info-box">
        <p>
          <strong>Social Security claiming age matters:</strong> Benefits are reduced ~6-7% per year
          before Full Retirement Age (67 for most), and increased 8% per year if delayed to age 70.
        </p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Claiming Strategy</label>
          <select
            value={ssClaimingStrategy}
            onChange={e => setSSClaimingStrategy(e.target.value as any)}
          >
            <option value="optimize">Optimize (Calculate Best Age)</option>
            <option value="early">Early (Age 62)</option>
            <option value="fra">Full Retirement Age (67)</option>
            <option value="delayed">Delayed (Age 70)</option>
          </select>
        </div>
        {ssClaimingStrategy !== 'optimize' && ssClaimingStrategy !== 'early' && ssClaimingStrategy !== 'fra' && ssClaimingStrategy !== 'delayed' && (
          <div className="form-group">
            <label>Custom Claiming Age</label>
            <input
              type="number"
              value={customSSAge}
              onChange={e => setCustomSSAge(parseInt(e.target.value) || 67)}
              min={62}
              max={70}
            />
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Years of Work History</label>
          <input
            type="number"
            value={ssWorkYears}
            onChange={e => setSSWorkYears(parseInt(e.target.value) || 20)}
            min={0}
            max={45}
          />
          <span className="hint">Social Security uses your highest 35 years of earnings</span>
        </div>
        <div className="form-group">
          <label>Estimated Monthly PIA (Optional)</label>
          <div className="input-with-prefix">
            <span className="prefix">$</span>
            <input
              type="text"
              value={estimatedPIA?.toLocaleString() || ''}
              onChange={e => setEstimatedPIA(parseNumber(e.target.value) || undefined)}
              placeholder="Leave blank to estimate"
            />
          </div>
          <span className="hint">Check ssa.gov/myaccount for your actual estimate</span>
        </div>
      </div>

      {filingStatus === 'married' && (
        <>
          <h3>Social Security - Spouse Benefits</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Spouse Claiming Strategy</label>
              <select
                value={spouseSSStrategy}
                onChange={e => setSpouseSSStrategy(e.target.value as any)}
              >
                <option value="optimize">Optimize (Calculate Best Age)</option>
                <option value="early">Early (Age 62)</option>
                <option value="fra">Full Retirement Age (67)</option>
                <option value="delayed">Delayed (Age 70)</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Spouse Estimated Monthly PIA (Optional)</label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="text"
                  value={spouseEstimatedPIA?.toLocaleString() || ''}
                  onChange={e => setSpouseEstimatedPIA(parseNumber(e.target.value) || undefined)}
                  placeholder="Leave blank to estimate"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderSpendingSection = () => (
    <div className="form-section">
      <h3>Spending in Retirement</h3>

      <div className="form-row">
        <div className="form-group">
          <label>Annual Spending (Today's Dollars)</label>
          <div className="input-with-prefix">
            <span className="prefix">$</span>
            <input
              type="text"
              value={annualSpending.toLocaleString()}
              onChange={e => setAnnualSpending(parseNumber(e.target.value))}
            />
          </div>
          <span className="hint">Your target annual spending in retirement</span>
        </div>
        <div className="form-group">
          <label>Healthcare Costs (Pre-Medicare)</label>
          <div className="input-with-prefix">
            <span className="prefix">$</span>
            <input
              type="text"
              value={healthcareCost.toLocaleString()}
              onChange={e => setHealthcareCost(parseNumber(e.target.value))}
            />
          </div>
          <span className="hint">Annual healthcare costs before Medicare at 65</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Spending Scenario</label>
          <select
            value={spendingScenario}
            onChange={e => setSpendingScenario(e.target.value as any)}
          >
            <option value="exact">Exact (Fixed Spending)</option>
            <option value="fluctuating">Fluctuating (90-120% variation)</option>
            <option value="increased">Increased (100-120% variation)</option>
          </select>
          <span className="hint">How your spending might vary year-to-year</span>
        </div>
      </div>

      <div className="spending-comparison">
        <h4>Spending Comparison</h4>
        <div className="comparison-grid">
          <div className="comparison-item">
            <span className="label">Monthly Spending</span>
            <span className="value">{formatCurrency(annualSpending / 12)}</span>
          </div>
          <div className="comparison-item">
            <span className="label">% of Current Income</span>
            <span className="value">{((annualSpending / currentIncome) * 100).toFixed(0)}%</span>
          </div>
          <div className="comparison-item">
            <span className="label">Withdrawal Rate (on current savings)</span>
            <span className="value">
              {((annualSpending / (traditional401k + traditionalIRA + roth401k + rothIRA + hsa + taxableBrokerage)) * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStrategiesSection = () => (
    <div className="form-section">
      <h3>Withdrawal Strategy</h3>

      <div className="strategy-cards">
        {[
          { id: 'tax_efficient', name: 'Tax-Efficient', desc: 'Fill low brackets with traditional, supplement with Roth. Recommended for most.' },
          { id: 'traditional_first', name: 'Traditional First', desc: 'Withdraw traditional accounts first. Simple but may result in higher taxes.' },
          { id: 'roth_first', name: 'Roth First', desc: 'Withdraw Roth first. Rarely optimal but preserves traditional growth.' },
          { id: 'proportional', name: 'Proportional', desc: 'Withdraw proportionally from all accounts. Balanced approach.' },
        ].map(strategy => (
          <div
            key={strategy.id}
            className={`strategy-card ${withdrawalStrategy === strategy.id ? 'selected' : ''}`}
            onClick={() => setWithdrawalStrategy(strategy.id as any)}
          >
            <div className="radio-indicator" />
            <div className="strategy-info">
              <span className="name">{strategy.name}</span>
              <span className="desc">{strategy.desc}</span>
            </div>
          </div>
        ))}
      </div>

      <h3>Roth Conversion Strategy</h3>
      <div className="info-box">
        <p>
          <strong>Roth conversions</strong> move money from Traditional to Roth accounts.
          You pay taxes now, but future withdrawals are tax-free. Best done in low-income years.
        </p>
      </div>

      <div className="strategy-cards">
        {[
          { id: 'none', name: 'No Conversions', desc: 'Don\'t convert. Keep traditional accounts as-is.' },
          { id: 'fill_bracket', name: 'Fill Tax Bracket', desc: 'Convert enough to fill your current tax bracket. Balanced approach.' },
          { id: 'avoid_irmaa', name: 'Avoid IRMAA', desc: 'Keep income below Medicare IRMAA thresholds to avoid premium surcharges.' },
          { id: 'aggressive', name: 'Aggressive', desc: 'Convert as much as possible. May result in higher taxes now but lower later.' },
        ].map(strategy => (
          <div
            key={strategy.id}
            className={`strategy-card ${rothConversionStrategy === strategy.id ? 'selected' : ''}`}
            onClick={() => setRothConversionStrategy(strategy.id as any)}
          >
            <div className="radio-indicator" />
            <div className="strategy-info">
              <span className="name">{strategy.name}</span>
              <span className="desc">{strategy.desc}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="form-row">
        <div className="form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="avoidIRMAA"
              checked={avoidIRMAA}
              onChange={e => setAvoidIRMAA(e.target.checked)}
            />
            <label htmlFor="avoidIRMAA">
              Avoid Medicare IRMAA
              <span className="hint">Keep income below thresholds to avoid premium surcharges</span>
            </label>
          </div>
        </div>
      </div>

      <h3>Simulation Settings</h3>
      <div className="form-row">
        <div className="form-group">
          <label>Number of Monte Carlo Scenarios</label>
          <select value={numScenarios} onChange={e => setNumScenarios(parseInt(e.target.value))}>
            <option value={50}>50 (Fast)</option>
            <option value={100}>100 (Recommended)</option>
            <option value={250}>250 (More Accurate)</option>
            <option value={500}>500 (High Accuracy)</option>
          </select>
        </div>
      </div>

      <button className="btn btn-secondary" onClick={compareStrategies} disabled={loading}>
        Compare Withdrawal Strategies
      </button>
    </div>
  );

  const renderResults = () => {
    if (!simulationResults) {
      return (
        <div className="no-results">
          <p>Run a simulation to see your retirement projections.</p>
          <button className="btn btn-primary" onClick={() => setActiveTab('inputs')}>
            Go to Inputs
          </button>
        </div>
      );
    }

    const { summary_stats, risk_analysis, recommendations, sample_scenario, optimal_ss_claiming_age, optimal_roth_conversion_strategy } = simulationResults;

    return (
      <div className="results-container">
        {/* Success Rate Banner */}
        <div className={`success-banner ${summary_stats.success_rate >= 0.9 ? 'success' : summary_stats.success_rate >= 0.75 ? 'warning' : 'danger'}`}>
          <div className="success-rate">
            <span className="value">{(summary_stats.success_rate * 100).toFixed(0)}%</span>
            <span className="label">Success Rate</span>
          </div>
          <div className="success-details">
            <p>
              {summary_stats.scenarios_successful} of {summary_stats.total_scenarios} scenarios
              had sufficient funds through retirement.
            </p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="metrics-grid">
          <div className="metric-card">
            <span className="label">Median Final Balance</span>
            <span className="value">{formatCurrency(summary_stats.median_final_balance)}</span>
          </div>
          <div className="metric-card">
            <span className="label">10th Percentile</span>
            <span className="value">{formatCurrency(summary_stats.percentile_10_balance)}</span>
            <span className="hint">Worst 10% of scenarios</span>
          </div>
          <div className="metric-card">
            <span className="label">90th Percentile</span>
            <span className="value">{formatCurrency(summary_stats.percentile_90_balance)}</span>
            <span className="hint">Best 10% of scenarios</span>
          </div>
          <div className="metric-card">
            <span className="label">Optimal SS Claiming Age</span>
            <span className="value">{optimal_ss_claiming_age}</span>
          </div>
          <div className="metric-card">
            <span className="label">Total Taxes Paid (Avg)</span>
            <span className="value">{formatCurrency(summary_stats.mean_taxes_paid)}</span>
          </div>
          <div className="metric-card">
            <span className="label">Roth Conversions (Avg)</span>
            <span className="value">{formatCurrency(summary_stats.mean_roth_conversions)}</span>
          </div>
        </div>

        {/* Risk Analysis */}
        <div className="risk-section">
          <h3>Risk Analysis</h3>
          <div className="risk-grid">
            <div className="risk-item">
              <span className="label">Worst Case Balance</span>
              <span className="value">{formatCurrency(risk_analysis.worst_case_balance)}</span>
            </div>
            <div className="risk-item">
              <span className="label">Best Case Balance</span>
              <span className="value">{formatCurrency(risk_analysis.best_case_balance)}</span>
            </div>
            <div className="risk-item">
              <span className="label">Probability of Ruin</span>
              <span className={`value ${risk_analysis.probability_of_ruin > 10 ? 'danger' : ''}`}>
                {risk_analysis.probability_of_ruin.toFixed(1)}%
              </span>
            </div>
            {risk_analysis.earliest_depletion_year && (
              <div className="risk-item">
                <span className="label">Earliest Depletion</span>
                <span className="value">Year {risk_analysis.earliest_depletion_year}</span>
              </div>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="recommendations-section">
          <h3>Recommendations</h3>
          <ul className="recommendations-list">
            {recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        </div>

        {/* Year-by-Year Table */}
        <div className="yearly-section">
          <h3>Sample Scenario - Year by Year</h3>
          <div className="table-wrapper">
            <table className="yearly-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Age</th>
                  <th>Employment</th>
                  <th>Social Security</th>
                  <th>Withdrawals</th>
                  <th>Taxes</th>
                  <th>Spending</th>
                  <th>Portfolio</th>
                </tr>
              </thead>
              <tbody>
                {sample_scenario.yearly_cashflows.map((year, i) => (
                  <tr key={i} className={year.total_portfolio_value <= 0 ? 'depleted' : ''}>
                    <td>{year.year}</td>
                    <td>{year.age}</td>
                    <td className={year.employment_income > 0 ? 'positive' : ''}>{formatCurrency(year.employment_income)}</td>
                    <td className={year.social_security_income > 0 ? 'positive' : ''}>{formatCurrency(year.social_security_income)}</td>
                    <td>{formatCurrency(year.traditional_withdrawal + year.roth_withdrawal)}</td>
                    <td className="negative">{formatCurrency(year.total_tax)}</td>
                    <td className="negative">{formatCurrency(year.annual_spending)}</td>
                    <td className={year.total_portfolio_value > 0 ? 'positive' : 'negative'}>
                      {formatCurrency(year.total_portfolio_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderStrategiesTab = () => {
    if (!withdrawalStrategies) {
      return (
        <div className="no-results">
          <p>Compare withdrawal strategies to see which approach might work best for you.</p>
          <button className="btn btn-secondary" onClick={compareStrategies} disabled={loading}>
            Compare Strategies
          </button>
        </div>
      );
    }

    return (
      <div className="strategies-container">
        <h2>Withdrawal Strategy Comparison</h2>
        <p className="subtitle">Based on your account balances and spending needs</p>

        <div className="strategies-grid">
          {withdrawalStrategies.strategies.map((strategy, i) => (
            <div key={i} className={`strategy-comparison-card ${strategy.name === 'Tax-Efficient' ? 'recommended' : ''}`}>
              {strategy.name === 'Tax-Efficient' && <div className="badge">Recommended</div>}
              <h3>{strategy.name}</h3>
              <p className="description">{strategy.description}</p>

              <div className="metrics">
                <div className="metric">
                  <span className="label">Annual Tax</span>
                  <span className="value negative">{formatCurrency(strategy.annual_tax)}</span>
                </div>
                <div className="metric">
                  <span className="label">Net Withdrawal</span>
                  <span className="value positive">{formatCurrency(strategy.net_withdrawal)}</span>
                </div>
              </div>

              <div className="pros-cons">
                <div className="pros">
                  <h4>Pros</h4>
                  <ul>
                    {strategy.pros.map((pro, j) => <li key={j}>{pro}</li>)}
                  </ul>
                </div>
                <div className="cons">
                  <h4>Cons</h4>
                  <ul>
                    {strategy.cons.map((con, j) => <li key={j}>{con}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="strategy-recommendation">
          <p>{withdrawalStrategies.recommendation}</p>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  return (
    <div className="us-retirement-planner">
      <header className="planner-header">
        <h1>US Retirement Planner</h1>
        <p>Monte Carlo simulation with Social Security, 401(k), IRA, and Roth optimization</p>
      </header>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <nav className="tab-nav">
        <button
          className={activeTab === 'inputs' ? 'active' : ''}
          onClick={() => setActiveTab('inputs')}
        >
          Inputs
        </button>
        <button
          className={activeTab === 'results' ? 'active' : ''}
          onClick={() => setActiveTab('results')}
          disabled={!simulationResults}
        >
          Results
        </button>
        <button
          className={activeTab === 'strategies' ? 'active' : ''}
          onClick={() => setActiveTab('strategies')}
        >
          Strategies
        </button>
      </nav>

      <main className="planner-content">
        {activeTab === 'inputs' && renderInputSection()}
        {activeTab === 'results' && renderResults()}
        {activeTab === 'strategies' && renderStrategiesTab()}
      </main>

      <footer className="planner-footer">
        <p className="disclaimer">
          This calculator is for educational purposes only. Consult a qualified financial advisor
          for personalized retirement planning advice.
        </p>
      </footer>
    </div>
  );
};

export default USRetirementPlanner;
